import React, { useMemo } from 'react'
import { useVenti } from 'venti'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { Invite, SiteConfiguration } from '@matech/thebigpos-sdk'
import { ModalWizard } from './ModalWizard'
import { Roles } from '../../services/client'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { routeTypes } from '../../pages/loanapp/LoanApp'
import { useAppContext } from '../AppContext'
import { getTheme } from '../../config'
import queryKeys from '../../services/queryKeys'
import useUser from '../../hooks/useUser'
import { WorkflowQuestionBaseProps } from '../../pages/admin/Workflows/utils/types'
import { FixMeLater } from '../../types'
import { useLoanContext } from '../../contexts/LoanContext'
import { getValidFormJson } from '../../services/helpers'

const theme = getTheme()

const ModalEConsent = () => {
	const ventiState = useVenti()
	const { state } = useAppContext()
	const { user } = useUser()
	const queryClient = useQueryClient()
	const { siteConfig, invite } = state as {
		siteConfig: SiteConfiguration | null
		invite?: Invite | null
	}
	const { loanId, loanData: legacyLoan, loan } = useLoanContext()

	const [isCompleted, setIsCompleted] = React.useState<boolean>(false)

	const { data: eConsentForm } = useQuery({
		queryKey: [queryKeys.workflow, siteConfig?.id],
		queryFn: () =>
			TheBigPOSApi.getFormBySiteConfigurationSlug({
				siteConfigurationId: siteConfig?.id ?? '',
				formType: routeTypes.indexOf('EConsent'),
			}),
		enabled: !!siteConfig?.id,
	})

	const eConsentFormData = eConsentForm?.data

	const userLoan = loan?.userLoans?.find(
		(ul) => ul?.user?.id === user.id
	)

	const hideCreditAuth =
		legacyLoan?.CreditAuthed === 'True' ||
		userLoan?.role === 'NonBorrower'
	const hideTCPAOptIn = legacyLoan?.BorrowerTCPAOptIn === 'Y'

	const consentQuestions = useMemo(() => {
		const questions = getValidFormJson(eConsentFormData?.formJSON)
		const newQuestions: WorkflowQuestionBaseProps[] = []

		questions.forEach(
			(q: WorkflowQuestionBaseProps, index: number) => {
				if (
					(hideCreditAuth && q.fieldId === 'BorrowerCreditAuth') ||
					(hideTCPAOptIn && q.fieldId === 'BorrowerTCPAOptIn')
				) {
					if (questions[index + 1]?.qId) {
						questions[index - 1].target = questions[index + 1].qId
					} else {
						questions[index - 1].target = ''
					}
				} else {
					newQuestions.push(q)
				}
			}
		)

		return newQuestions
	}, [eConsentFormData?.formJSON, hideCreditAuth, hideTCPAOptIn])

	const updateLoanMutation = useMutation({
		mutationFn: (data: FixMeLater) =>
			TheBigPOSApi.updateLoanConsent(loanId, {
				LoanID: loanId,
				SiteConfigurationID: siteConfig?.id,
				...data,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.loanData, loanId],
			})
			ventiState.set(theme.storageKeys.pleaseWaitMessage, '')
			ventiState.set(theme.storageKeys.successObject, {
				message: `Authorization was successful. Access to your account has been enabled`,
			})
		},
		onError: () => {
			ventiState.set(theme.storageKeys.pleaseWaitMessage, '')
			ventiState.set(
				theme.storageKeys.infoMessage,
				'It looks like your loan is currently being worked on. Please check back again later.'
			)
		},
	})

	const handleSendEConsent = (data: FixMeLater) => {
		ventiState.set(
			theme.storageKeys.pleaseWaitMessage,
			'Please wait while we verify your account'
		)
		updateLoanMutation.mutate(data)
		setIsCompleted(true)
	}

	if (
		isCompleted ||
		!legacyLoan ||
		legacyLoan.EConsented !== 'False' ||
		(invite && !invite.completedLoanApplication) ||
		consentQuestions.length < 1
	) {
		return null
	}

	return (
		<ModalWizard
			modalTitle="Welcome to the Borrower Portal!"
			finalButtonText="Finish"
			questions={consentQuestions}
			modal={Roles.borrower === user.role}
			setModal={() => false}
			save={handleSendEConsent}
			type={undefined}
			modalData={undefined}
		/>
	)
}

export default ModalEConsent
